import type {
  AiApplicationResult,
  AiBatchHeaderDecision,
  AiBatchWorkbookColumn,
  AiBatchWorkbookResult,
  AiBatchWorkbookRow,
  CreateAiApplicationBatchInput,
} from '@/api/payouts';
import { DEFAULT_APPLICATION_REFERENCE } from '@/domain/payouts';

export type AiBatchRowStatus = 'pending' | 'parsing' | 'failed' | 'needs_input' | 'ready';
const VALID_AMOUNT_RE = /^(0|[1-9]\d{0,17})(\.\d{1,2})?$/;

export interface AiBatchEditableRow extends AiBatchWorkbookRow {
  status: AiBatchRowStatus;
  result: AiApplicationResult | null;
  error: string | null;
  manualFields: Set<string>;
  groupingConfirmed: boolean;
}

export interface ParsedPastedTable {
  sheetName: '粘贴内容';
  headerDetected: boolean;
  headerConfidence: 'high' | 'low';
  firstNonEmptyRowNumber: number;
  columns: AiBatchWorkbookColumn[];
  rows: AiBatchWorkbookRow[];
}

export interface ParsedClipboardTable {
  text: string;
  rowCount: number;
  columnCount: number;
}

const HEADER_HINTS = [
  'name', 'account', 'swift', 'bic', 'iban', 'bank', 'currency', 'amount', 'address', 'zip',
  '姓名', '收款人', '账户', '账号', '银行', '币种', '金额', '地址', '邮编', '附言', '备注',
];

function excelColumn(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }
  return out;
}

function headerLike(cells: readonly string[]): boolean {
  const nonEmpty = cells.map((cell) => cell.trim().toLowerCase()).filter(Boolean);
  if (nonEmpty.length === 0) return false;
  const hits = nonEmpty.filter((cell) => HEADER_HINTS.some((hint) => cell.includes(hint))).length;
  return hits >= Math.max(1, Math.ceil(nonEmpty.length * 0.4));
}

const CELL_BLOCK_TAGS = new Set(['DIV', 'LI', 'P']);

function clipboardCellText(cell: HTMLTableCellElement): string {
  let value = '';
  const newline = () => {
    if (value && !value.endsWith('\n')) value += '\n';
  };
  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      value += node.nodeValue ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as Element;
    if (element.tagName === 'BR') {
      newline();
      return;
    }
    Array.from(element.childNodes).forEach(visit);
    if (CELL_BLOCK_TAGS.has(element.tagName)) newline();
  };
  Array.from(cell.childNodes).forEach(visit);
  return value
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function quoteTsvCell(value: string): string {
  return /[\t\r\n"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function parseClipboardTableHtml(html: string): ParsedClipboardTable | null {
  if (!html.trim() || typeof DOMParser === 'undefined') return null;
  const document = new DOMParser().parseFromString(html, 'text/html');
  const table = document.querySelector('table');
  if (!table) return null;
  const rows = Array.from(table.rows)
    .map((row) => Array.from(row.cells).map(clipboardCellText))
    .filter((cells) => cells.length > 0);
  if (rows.length === 0) return null;
  return {
    text: rows.map((cells) => cells.map(quoteTsvCell).join('\t')).join('\n'),
    rowCount: rows.length,
    columnCount: Math.max(...rows.map((cells) => cells.length)),
  };
}

interface PhysicalRow { rowNumber: number; cells: string[]; quotedMultiline: boolean }

function parseTsv(text: string): PhysicalRow[] {
  const rows: PhysicalRow[] = [];
  let cells: string[] = [];
  let cell = '';
  let quoted = false;
  let rowNumber = 1;
  let rowStart = 1;
  let quotedMultiline = false;
  const pushRow = () => {
    cells.push(cell.trim());
    if (cells.some(Boolean)) rows.push({ rowNumber: rowStart, cells, quotedMultiline });
    cells = [];
    cell = '';
    quotedMultiline = false;
  };

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (char === '"') {
      if (quoted && text[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (char === '\t' && !quoted) {
      cells.push(cell.trim());
      cell = '';
      continue;
    }
    if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      pushRow();
      rowNumber += 1;
      rowStart = rowNumber;
      continue;
    }
    if ((char === '\n' || char === '\r') && quoted) {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      cell += '\n';
      quotedMultiline = true;
      // 引号中的换行属于单元格内容，不推进表格的数据行号。
      continue;
    }
    cell += char;
  }
  if (cell || cells.length > 0) pushRow();
  return rows;
}

function primaryColumnCount(rows: readonly PhysicalRow[]): number {
  const frequencies = new Map<number, number>();
  rows.forEach((row) => frequencies.set(row.cells.length, (frequencies.get(row.cells.length) ?? 0) + 1));
  return [...frequencies.entries()]
    .sort(([aLength, aCount], [bLength, bCount]) => bCount - aCount || bLength - aLength)[0]?.[0] ?? 0;
}

function recoverRows(rows: readonly PhysicalRow[]): Array<PhysicalRow & { uncertain: boolean; warnings: string[] }> {
  const target = primaryColumnCount(rows);
  const recovered: Array<PhysicalRow & { uncertain: boolean; warnings: string[] }> = [];
  for (const row of rows) {
    const previous = recovered[recovered.length - 1];
    const leadingContinuation = row.cells[0] === '' && previous !== undefined;
    if (leadingContinuation) {
      const continuation = row.cells.slice(1).filter((cell, index, all) => cell !== '' || index < all.length - 1);
      if (previous.cells.length < target) {
        previous.cells.push(...continuation);
      } else {
        const last = previous.cells.length - 1;
        previous.cells[last] = `${previous.cells[last] ?? ''}\n${continuation.join('\t')}`.trim();
      }
      previous.uncertain = true;
      previous.warnings.push(`第 ${row.rowNumber} 个物理行已自动合并到上一条，请核对原始数据分组`);
      continue;
    }
    const completesPreviousRow = previous !== undefined
      && previous.cells.length < target
      && row.cells.length < target
      && previous.cells.length + row.cells.length - 1 === target;
    if (completesPreviousRow) {
      const last = previous.cells.length - 1;
      previous.cells[last] = `${previous.cells[last] ?? ''}\n${row.cells[0] ?? ''}`.trim();
      previous.cells.push(...row.cells.slice(1));
      previous.uncertain = true;
      previous.warnings.push(`第 ${row.rowNumber} 个物理行已自动合并到上一条，请核对原始数据分组`);
      continue;
    }
    recovered.push({ ...row, cells: [...row.cells], uncertain: false, warnings: [] });
  }
  for (const row of recovered) {
    if (row.cells.length !== target) {
      row.uncertain = true;
      row.warnings.push(`这一条有 ${row.cells.length} 列，主要数据为 ${target} 列，请核对列对应关系`);
    }
  }
  return recovered;
}

function buildColumns(count: number, header?: readonly string[]): AiBatchWorkbookColumn[] {
  const seen = new Set<string>();
  return Array.from({ length: count }, (_, index) => {
    const letter = excelColumn(index);
    let title = header?.[index]?.trim() || `第 ${letter} 列`;
    if (seen.has(title.toLowerCase())) title = `${title} (${letter})`;
    seen.add(title.toLowerCase());
    return { key: `col-${letter.toLowerCase()}`, title, excelColumn: letter };
  });
}

export function parsePastedTable(text: string, firstRowIsHeader?: boolean, maxRows = 50): ParsedPastedTable {
  // 不对整段做 trim：它会删掉首尾的 tab，从而改变第一列/最后一列的位置。
  // parseTsv 自己会忽略全空行，同时保留有数据行的空单元格。
  const physical = parseTsv(text);
  if (physical.length === 0) throw new Error('请先粘贴表格内容');
  const recovered = recoverRows(physical);
  const first = recovered[0]!;
  const autoHeader = headerLike(first.cells);
  const headerDetected = firstRowIsHeader ?? autoHeader;
  const body = headerDetected ? recovered.slice(1) : recovered;
  if (body.length === 0) throw new Error('粘贴内容只有表头，没有数据');
  if (body.length > maxRows) throw new Error(`一次最多解析 ${maxRows} 条，当前有 ${body.length} 条`);
  const count = Math.max(first.cells.length, ...body.map((row) => row.cells.length));
  const columns = buildColumns(count, headerDetected ? first.cells : undefined);
  const rows = body.map<AiBatchWorkbookRow>((row) => ({
    id: `row-${row.rowNumber}`,
    rowNumber: row.rowNumber,
    cells: Object.fromEntries(columns.map((column, index) => [column.key, row.cells[index]?.trim() || null])),
    unsafeCells: [],
    rowGroupingUncertain: row.uncertain,
    warnings: row.warnings,
  }));
  return {
    sheetName: '粘贴内容',
    headerDetected,
    headerConfidence: autoHeader ? 'high' : 'low',
    firstNonEmptyRowNumber: first.rowNumber,
    columns,
    rows,
  };
}

export function applyAiHeaderDecision<T extends ParsedPastedTable | AiBatchWorkbookResult>(
  table: T,
  decision: AiBatchHeaderDecision,
): T {
  if (!decision.firstRowIsHeader) {
    if (table.rows.length > 50) throw new Error(`一次最多解析 50 条，当前有 ${table.rows.length} 条`);
    return {
      ...table,
      headerDetected: false,
      headerConfidence: decision.confidence === 'high' ? 'high' : 'low',
    };
  }

  const header = table.rows[0];
  if (!header || table.rows.length === 1) throw new Error('AI 判断第一行为表头，但表格里没有数据行');
  const seen = new Set<string>();
  const nextColumns = table.columns.map((column) => {
    let title = header.cells[column.key]?.trim() || `第 ${column.excelColumn} 列`;
    if (seen.has(title.toLowerCase())) title = `${title} (${column.excelColumn})`;
    seen.add(title.toLowerCase());
    return { ...column, title };
  });
  const rows = table.rows.slice(1);
  if (rows.length > 50) throw new Error(`一次最多解析 50 条，当前有 ${rows.length} 条`);
  return {
    ...table,
    headerDetected: true,
    headerConfidence: decision.confidence === 'high' ? 'high' : 'low',
    columns: nextColumns,
    rows,
  };
}

export function aiHeaderSample(
  columns: readonly AiBatchWorkbookColumn[],
  rows: readonly AiBatchWorkbookRow[],
): string[][] {
  return rows.slice(0, 5).map((row) => columns.map((column) => row.cells[column.key] ?? ''));
}

export function serializeOriginalRow(columns: readonly AiBatchWorkbookColumn[], row: AiBatchWorkbookRow): string {
  return columns
    .map((column) => ({ title: column.title, value: row.cells[column.key]?.trim() }))
    .filter((item) => item.value)
    .map((item) => `${item.title}: ${item.value}`)
    .join('\n');
}

export function deriveBatchRowStatus(row: AiBatchEditableRow): AiBatchRowStatus {
  if (row.status === 'parsing') return 'parsing';
  if (row.error) return 'failed';
  if (!row.result) return 'pending';
  if (row.unsafeCells.length > 0) return 'needs_input';
  if (row.rowGroupingUncertain && !row.groupingConfirmed) return 'needs_input';
  const requiredMissing = row.result.payee.fields
    .filter((field) => field.required)
    .some((field) => !row.result!.payee.values[field.path]?.trim());
  if (requiredMissing) return 'needs_input';
  const amount = row.result.values.amount?.trim() ?? '';
  if (!VALID_AMOUNT_RE.test(amount) || /^0(?:\.0{1,2})?$/.test(amount)) return 'needs_input';
  if (!row.result.values.reference.trim() || row.result.values.reference.length > 128) return 'needs_input';
  return 'ready';
}

function amountToCents(amount: string): bigint {
  const [whole, fraction = ''] = amount.split('.');
  return BigInt(whole!) * 100n + BigInt((fraction + '00').slice(0, 2));
}

function centsToAmount(cents: bigint): string {
  const whole = cents / 100n;
  const fraction = String(cents % 100n).padStart(2, '0');
  return `${whole}.${fraction}`;
}

export function batchCurrencyTotals(rows: readonly AiBatchEditableRow[]) {
  const totals = new Map<string, { cents: bigint; count: number }>();
  for (const row of rows) {
    const amount = row.result?.values.amount?.trim() ?? '';
    if (!row.result?.currency || !VALID_AMOUNT_RE.test(amount) || /^0(?:\.0{1,2})?$/.test(amount)) continue;
    const current = totals.get(row.result.currency) ?? { cents: 0n, count: 0 };
    current.cents += amountToCents(amount);
    current.count += 1;
    totals.set(row.result.currency, current);
  }
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, total]) => ({ currency, amount: centsToAmount(total.cents), count: total.count }));
}

export function buildAiBatchCreateInput(
  token: string,
  rows: readonly AiBatchEditableRow[],
): CreateAiApplicationBatchInput {
  return {
    token,
    rows: rows.filter((row) => row.result).map((row) => ({
      ...(row.result!.sourceRecordId ? { sourceRecordId: row.result!.sourceRecordId } : {}),
      clientRowId: row.id,
      rowNumber: row.rowNumber,
      payeeValues: row.result!.payee.values,
      amount: row.result!.values.amount!.trim(),
      reference: row.result!.values.reference.trim() || DEFAULT_APPLICATION_REFERENCE,
      ...(row.result!.values.note?.trim() ? { note: row.result!.values.note!.trim() } : {}),
      feePaidBy: row.result!.values.feePaidBy,
      swiftChargeOption: row.result!.values.swiftChargeOption,
    })),
  };
}
